import { Node, mergeAttributes } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-2";
import Blockquote from "@tiptap/extension-blockquote";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { QUOTE_STYLE } from "../brand";
import { toHex } from "./formatting";
import ImageView from "../ImageView.vue";

// Citacao no padrao do Guia de Formatacao da casa. Toda citacao sai com o
// estilo em linha (o mesmo que o editor antigo gravava).
export const HouseQuote = Blockquote.extend({
    renderHTML({ HTMLAttributes }) {
        return ["blockquote", mergeAttributes(HTMLAttributes, { style: QUOTE_STYLE }), 0];
    },
});

// Quebra de pagina: no PDF/Word vira salto de folha; no editor, uma marca.
export const PageBreak = Node.create({
    name: "pageBreak",
    group: "block",
    atom: true,
    selectable: true,

    parseHTML() {
        return [{ tag: "div[data-page-break]" }];
    },

    renderHTML() {
        return ["div", { "data-page-break": "true", style: "page-break-after: always; break-after: page;" }];
    },

    addCommands() {
        return {
            setPageBreak: () => ({ chain }) => chain().insertContent({ type: this.name }).createParagraphNear().run(),
        };
    },

    addKeyboardShortcuts() {
        return { "Mod-Enter": () => this.editor.commands.setPageBreak() };
    },
});

// Celulas com cor de fundo/texto da paleta da marca (cabecalho destacado).
const cellColorAttributes = {
    backgroundColor: {
        default: null,
        parseHTML: (element) => toHex(element.style.backgroundColor) || null,
        renderHTML: (attributes) => (attributes.backgroundColor ? { style: `background-color: ${attributes.backgroundColor}` } : {}),
    },
    textColor: {
        default: null,
        parseHTML: (element) => toHex(element.style.color) || null,
        renderHTML: (attributes) => (attributes.textColor ? { style: `color: ${attributes.textColor}` } : {}),
    },
};

const cellBorder = "border: 1px solid #d0d5dd; padding: 6px 8px; vertical-align: top;";

export const StyledTableCell = TableCell.extend({
    addAttributes() {
        return { ...this.parent(), ...cellColorAttributes };
    },
    renderHTML({ HTMLAttributes }) {
        return ["td", mergeAttributes({ style: cellBorder }, HTMLAttributes), 0];
    },
});

export const StyledTableHeader = TableHeader.extend({
    addAttributes() {
        return { ...this.parent(), ...cellColorAttributes };
    },
    renderHTML({ HTMLAttributes }) {
        return ["th", mergeAttributes({ style: `${cellBorder} font-weight: bold; text-align: center;` }, HTMLAttributes), 0];
    },
});

export const StyledTable = Table.extend({
    renderHTML({ node, HTMLAttributes }) {
        const base = this.parent({ node, HTMLAttributes });
        // ["table", attrs, colgroup, ["tbody", 0]]
        base[1] = mergeAttributes(base[1], { style: "width: 100%; border-collapse: collapse;" });
        return base;
    },
}).configure({ resizable: false });

// Link que tambem guarda download (anexos inseridos no texto).
export const DocumentLink = Link.extend({
    addAttributes() {
        return {
            ...this.parent(),
            download: {
                default: null,
                parseHTML: (element) => element.getAttribute("download"),
                renderHTML: (attributes) => (attributes.download ? { download: attributes.download } : {}),
            },
        };
    },
}).configure({
    openOnClick: false,
    autolink: true,
    HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
    // Anexos do editor antigo sao links data: (arquivo embutido no HTML).
    isAllowedUri: (url, ctx) => /^data:[\w.+-]+\/[\w.+-]+;base64,/i.test(url) || ctx.defaultValidate(url),
});

// Imagens em base64, como o editor antigo; largura limitada a folha.
// A largura (px a 100% de zoom) e o alinhamento vao no proprio HTML, em
// atributos e no estilo em linha, para o PDF e o site lerem sem o editor.
export const IMAGE_ALIGNMENTS = ["left", "center", "right"];

function imageStyle(width, align) {
    const parts = ["max-width: 100%", "height: auto", "display: block"];
    if (width) parts.unshift(`width: ${width}px`);
    if (align === "center") parts.push("margin: 0 auto");
    if (align === "right") parts.push("margin: 0 0 0 auto");
    return `${parts.join("; ")};`;
}

function imageWidthFromElement(element) {
    const attribute = String(element.getAttribute("width") || "").trim();
    if (/^\d+(px)?$/.test(attribute)) return parseInt(attribute, 10) || null;
    const match = /(?:^|;)\s*width\s*:\s*(\d+(?:\.\d+)?)px/i.exec(element.getAttribute("style") || "");
    return match ? Math.round(Number(match[1])) || null : null;
}

function imageAlignFromElement(element) {
    const explicit = element.getAttribute("data-align");
    if (IMAGE_ALIGNMENTS.includes(explicit)) return explicit;
    const style = (element.getAttribute("style") || "").replace(/\s+/g, " ").toLowerCase();
    if (/margin: 0 auto;?/.test(style) || /margin-left: auto;.*margin-right: auto/.test(style)) return "center";
    if (/margin: 0 0 0 auto|margin-left: auto/.test(style)) return "right";
    return "left";
}

export const DocumentImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent(),
            width: { default: null, parseHTML: imageWidthFromElement, renderHTML: () => ({}) },
            align: { default: "left", parseHTML: imageAlignFromElement, renderHTML: () => ({}) },
        };
    },
    renderHTML({ node, HTMLAttributes }) {
        const { width, align } = node.attrs;
        return ["img", mergeAttributes(HTMLAttributes, {
            width: width || null,
            "data-align": align && align !== "left" ? align : null,
            style: imageStyle(width, align),
        })];
    },
    addCommands() {
        return {
            ...this.parent(),
            setImageAlign: (align) => ({ commands }) => commands.updateAttributes(this.name, { align }),
            setImageWidth: (width) => ({ commands }) => commands.updateAttributes(this.name, { width: width || null }),
        };
    },
    addKeyboardShortcuts() {
        // Mesmos atalhos do alinhamento de texto, quando a selecao e uma imagem.
        const alignImage = (align) => () => this.editor.isActive(this.name) && this.editor.commands.setImageAlign(align);
        return { "Mod-Shift-l": alignImage("left"), "Mod-Shift-e": alignImage("center"), "Mod-Shift-r": alignImage("right") };
    },
    addNodeView() {
        return VueNodeViewRenderer(ImageView);
    },
}).configure({ allowBase64: true, inline: false });
